// Live preview of how an invoice looks under a given template + the company's
// business settings. Purely presentational — sample customer/line data is baked in
// so a founder sees the real styling with their own business identity/bank details.

import { C } from "../customers/theme";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const SAMPLE_LINES = [
  { desc: "Consulting — August", qty: 10, price: 85, vat: 23 },
  { desc: "Support retainer", qty: 1, price: 250, vat: 23 },
];

export default function InvoiceTemplatePreview({ template = {}, business = {} }) {
  const accent = template.accent_color || C.primary;
  const style = template.layout_style || "classic";
  const bordered = style !== "minimal";
  const name = business.trading_name || business.legal_name || "Your Business";
  const addr = [business.address_line1, business.address_line2, [business.city, business.postal_code].filter(Boolean).join(" "), business.country].filter(Boolean);

  const subtotal = SAMPLE_LINES.reduce((s, l) => s + l.qty * l.price, 0);
  const vat = SAMPLE_LINES.reduce((s, l) => s + l.qty * l.price * (l.vat / 100), 0);
  const total = subtotal + vat;

  const th = { textAlign: "left", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, color: "#667", padding: "5px 6px", borderBottom: `2px solid ${accent}` };
  const td = { fontSize: 10, padding: "5px 6px", borderBottom: bordered ? "1px solid #e5e7eb" : "none", color: "#222" };

  return (
    <div aria-label="Invoice preview" style={{ background: C.white, color: "#222", borderRadius: 8, padding: 22, width: "100%", boxShadow: "0 1px 6px rgba(0,0,0,0.12)", fontFamily: "Arial, sans-serif" }}>
      {/* Header */}
      {style === "modern" && <div style={{ height: 6, background: accent, borderRadius: 3, marginBottom: 14 }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {template.show_logo && business.logo_url && <img src={business.logo_url} alt="" style={{ maxWidth: 64, maxHeight: 64 }} />}
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: style === "minimal" ? "#111" : accent }}>{name}</div>
            {addr.map((l, i) => <div key={i} style={{ fontSize: 10, color: "#555" }}>{l}</div>)}
            {business.vat_number && <div style={{ fontSize: 10, color: "#555" }}>VAT {business.vat_number}</div>}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: style === "minimal" ? "#111" : accent, letterSpacing: 1 }}>INVOICE</div>
          <div style={{ fontSize: 10, color: "#555" }}>INV-00001</div>
          <div style={{ fontSize: 10, color: "#555" }}>Issued: 2026-07-01</div>
        </div>
      </div>

      <div style={{ marginTop: 14, fontSize: 10, color: "#555" }}>
        <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, color: "#889" }}>Bill to</div>
        <div style={{ fontWeight: 700, color: "#222", fontSize: 11 }}>Acme Distribution Ltd</div>
        <div>12 Market Street, Dublin 2</div>
      </div>

      {/* Lines */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
        <thead><tr><th style={th}>Description</th><th style={{ ...th, textAlign: "right" }}>Qty</th><th style={{ ...th, textAlign: "right" }}>Price</th><th style={{ ...th, textAlign: "right" }}>Total</th></tr></thead>
        <tbody>
          {SAMPLE_LINES.map((l, i) => (
            <tr key={i}><td style={td}>{l.desc}</td><td style={{ ...td, textAlign: "right" }}>{l.qty}</td><td style={{ ...td, textAlign: "right" }}>{money(l.price)}</td><td style={{ ...td, textAlign: "right" }}>{money(l.qty * l.price)}</td></tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
        <div style={{ width: 200, fontSize: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: "#555" }}><span>Subtotal</span><span>{money(subtotal)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: "#555" }}><span>VAT</span><span>{money(vat)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", marginTop: 3, borderTop: `2px solid ${accent}`, fontWeight: 800, fontSize: 12 }}><span>Total</span><span style={{ color: style === "minimal" ? "#111" : accent }}>{money(total)}</span></div>
        </div>
      </div>

      {/* Bank details */}
      {template.show_bank_details && (
        <div style={{ marginTop: 14, padding: 10, background: "#f7f8fa", borderRadius: 6, fontSize: 10, color: "#444" }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>Payment details</div>
          <div>{business.bank_name || "Bank"} · {business.bank_account_name || name}</div>
          <div>IBAN {business.iban || "IE00 XXXX 0000 0000 0000 00"}{business.bic ? ` · BIC ${business.bic}` : ""}</div>
        </div>
      )}

      {/* Terms + footer */}
      {template.show_terms && (business.default_terms_text || template.footer_text || business.invoice_footer) && (
        <div style={{ marginTop: 12, fontSize: 9.5, color: "#777", borderTop: bordered ? "1px solid #eee" : "none", paddingTop: 8 }}>
          {business.default_terms_text && <div>{business.default_terms_text}</div>}
          {(template.footer_text || business.invoice_footer) && <div style={{ marginTop: 4, textAlign: "center", color: accent, fontWeight: 600 }}>{template.footer_text || business.invoice_footer}</div>}
        </div>
      )}
    </div>
  );
}
